/* Серверный режим экрана «Текст» на движке шаблона (план вехи 3, шаги 2–4 экрана; lane plan text-cover §2.2).
   createText(createTextServer) (features/text/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SRV спрашивает этот объект:
   какой инструмент, списки выборов, цена, готовность, файлы, создание, хвост и итог — только из формы и сервиса заказа кадра (OrderService
   привязки EngineFrame). Правила:
   - одна привязка — один кадр ServerStudio — одно приложение — один инструмент шаблона (S.tool по политике формы: transcription → stt,
     product_description → seo, prompt_from_photo → pphoto, prompt_from_video → pvideo, prompt_create → pnew, improve → pfix); другой
     инструмент (setTool шаблона) — другое приложение: openStudioTarget, новый кадр (Q-T3);
   - у кадра своё состояние формы (S), свой мост загрузки, своё зеркало входов, свой черновик и свой хвост BotRun: файлы, согласие, заказ
     и варианты одного приложения или владельца не видны и не уходят под другим;
   - S — вид входов сервиса: входы изменились не формой (повтор из «Истории», черновик, заявка родной формы, «Изменить») — readText → S;
     правка формы — writeText → входы (одна точка записи — syncFoot шаблона), только опубликованными ключами (text-form-sync.ts);
   - движок не пишет в sessionStorage заявки и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис; черновик
     `steos:text:draft:<owner>:<app>` — тот же, что у родной формы (text-draft.ts);
   - цена на кнопке — цена формы или расчёта сервера; у расшифровки до расчёта — минуты файла × цена приложения за минуту (шаблон),
     у ссылки — youtube_price; в заказ уходит цена сервиса; если они расходятся у платной работы, создание закрыто: «Цена этого выбора пока
     не подтверждена» (D11);
   - файлы — мост загрузки (media-bridge.ts): расшифровка — одна рамка (звук или видео по типу файла, другой источник и ссылка снимаются),
     описание товара — лоток photo…photo_N, промпт из фото — фото, из видео — ролик и начало фрагмента from_ms; лист «Права на материалы»,
     одна очередь, «Продолжить загрузку» тем же id; файлы повтора — превью сервера (видео — загрузить заново, решение Q7);
   - «Создать промпт» и «Улучшить промпт» — варианты на месте (без страницы заказа): заказ ведёт тот же сервис, варианты — из итога заказа. */
import { endpoints } from '../../api';
import { uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, markSeen, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { tailCurrent, tailError, tailReceipt, tailSteps, cNum } from '../studio/circle/order-tail-model';
import { toolField } from '../studio/circle/tool-form';
import { phaseOf } from '../studio/engine-kit/run-driver';
import { type StudioApp, type StudioForm } from '../studio/studio-api';
import { record } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { improveInline, setTextImprove } from '../studio/engine-kit/improve-service';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaFile, mediaNote, mediaPolicy, type MediaBridge, type MediaItem, type MediaPolicy } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { filePeaks } from '../voice/voice-model';
import { choiceLabels, clipText, textKeys, textKind, textMissing, textPolicy, wishesLimit, youtubeId, type TextKind } from './native/text-model';
import { scriptOf, textDocumentPlain, textTailFail, type TextDocument } from './native/text-result-model';
import { openProjectsWith } from '../script/Projects';
import { createTextMore, type TextMore } from './text-more';
import { createTextResults, type TextResults } from './text-result';
import { readingHTML, variantsOf, type ReadingContext } from './text-reading-html';
import { createTextDraft, type TextDraft } from './text-draft';
import {
  fillTextLists,
  inlineTool,
  listsBase,
  readText,
  toolOf,
  toolOfEntry,
  writeFrom,
  writeText,
  type PromptView,
  type TextListsBase,
  type TextTool,
  type TextView,
} from './text-form-sync';
import { PROMPT_MAX } from '../studio/prompt-limit';

/* ---------- контракт с движком (features/text/engine.ts, serve({...})) ---------- */

/** Файл в рамке (S.stt.file, S.seo.files[], S.pphoto.file, S.pvideo.clip шаблона): mid — запись моста загрузки */
export interface TextFile {
  name: string;
  size: number;
  url: string;
  kind: 'audio' | 'video' | 'photo';
  dur?: number;
  peaks?: number[];
  poster?: string;
  video?: string;
  w?: number;
  h?: number;
  rec?: boolean;
  trim?: { from: number; to: number };
  mid?: string;
}
/** Варианты на месте (T().res шаблона): st шаблона run | done | mod | fail и свои сервера unk (исход не подтверждён), rej (заказ не создан),
    err (итог не открылся); id — заказ этих вариантов */
export interface PromptRes {
  st: 'run' | 'done' | 'mod' | 'fail' | 'unk' | 'rej' | 'err';
  sig: string;
  snap: { tool: string; target: string; tpl: string[]; own: string; text: string; n: number };
  price: number;
  vars?: Array<{ h: string; t: string }>;
  test?: null;
  id?: number;
  error?: string;
}
interface PromptState {
  text: string;
  target: string;
  tpl: string[];
  own: boolean;
  ownText: string;
  res: PromptRes | null;
}
/** Состояние формы шаблона (S) */
export interface TextState {
  tool: TextTool;
  ytWant: boolean;
  long: boolean;
  v: number;
  stt: { file: TextFile | null; lang: string; spk: boolean; tc: boolean };
  yt: { url: string; err: boolean; kind: string; lang: string };
  seo: { files: TextFile[]; text: string; name: string; feat: string; market: string | null; tone: string; wish: string };
  pphoto: { file: TextFile | null; style: string; lang: string };
  pvideo: { clip: TextFile | null; style: string; lang: string };
  pnew: PromptState;
  pfix: PromptState;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EngineUpload {
  o: { mb?: number; multi?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
interface EngineSettings {
  sync(o?: { force?: boolean }): void;
  close(): void;
}
/** Выбор строки «Ещё настройки» (settingsCard шаблона) */
export interface SettingItem {
  k: string;
  kind: string;
  opts?: Array<string[]>;
  improve?: string;
  max?: number;
  [key: string]: unknown;
}
export interface ToneCard {
  id: string;
  n: string;
  h: string;
  t: string;
  b: string;
}
/** Крючки, которые движок отдаёт serve (engine.ts, вставка после durSync): tail, foot, sl и rail — геттеры */
export interface TextEngineHooks {
  S: TextState;
  REC: { st: 'idle' | 'asking' | 'rec'; sec: number };
  CAM: { st: 'idle' | 'asking' | 'on'; on: string | null; sim: boolean };
  VT: { on: boolean; close(): void };
  OPT: Record<string, string[][]>;
  TPL: Record<string, Record<string, string[][]>>;
  TPL_X: Record<string, string>;
  FOR: string[][];
  BOX: Record<string, { f: string; i: string; l: string; min: number; max: number; ph: string; foot: string }>;
  TONES: ToneCard[];
  TOOLS: string[][];
  R: Record<string, Record<string, string>>;
  /** Идеи «Создать промпт» [подпись, текст], заготовки преимуществ товара, пожелания к описанию [подпись, фраза] — шаблона, переводятся на месте */
  IDEAS: string[][];
  FEATS: string[];
  SEO_WISH: string[][];
  UNIT(): Record<string, string>;
  VERB(): Record<string, string>;
  readonly tail: BotRunApi;
  readonly foot: EngineFoot;
  readonly sl: EngineSettings;
  readonly rail: { html(): string };
  ups(): Record<'stt' | 'seo' | 'pphoto' | 'pvideo', EngineUpload>;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  T(): Record<string, unknown>;
  paintPage(): void;
  renderBody(anim?: boolean): void;
  paintUp(): void;
  paintVars(anim?: boolean): void;
  footRedo(): void;
  syncFoot(): void;
  syncBox(): void;
  syncLink(inFrame?: boolean): void;
  paintTitle(): void;
  liveStop(): void;
  recCancel(quiet?: boolean): void;
  camStop(): void;
  varsInView(): void;
  fitSync(): void;
  trimPaint(): void;
  durSync(): void;
  glide(fn: () => void): void;
  kindNow(): string;
  srcOf(): '' | 'file' | 'link';
  ytId(url: string): string;
  sigOf(t: Pick<PromptState, 'target' | 'tpl' | 'own' | 'ownText' | 'text'>): string;
  gSnap(): PromptRes['snap'];
  seoParse(): void;
  seoCompose(): void;
  clipDur(c: TextFile | null): number;
  seoMarket(): string;
  isPlat(v: string): boolean;
  copyAct(btn: HTMLElement, text: string, node: Element | null): void;
  clipText(text: string, max: number): string;
  rhHTML(b: string, sm: string, i?: number): string;
  parasHTML(L: Array<[number, string]>, tc: boolean): string;
  ulHTML(L: string[]): string;
  cp(text: string): number;
  cpReset(): void;
  waveArt(f: { peaks?: number[]; dur?: number }): string;
  docArt(): string;
  setRecMax(sec: number): void;
  recMax(): number;
  clipMax(): number;
}
type Missing = 'load' | 'blocked' | 'off' | 'unavailable' | '';
type UpTool = 'stt' | 'seo' | 'pphoto' | 'pvideo';

export interface TextServer {
  ui<U extends object>(base: U): ServerUi<U>;
  open(o: object): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  has(k: 'url' | 'tray' | 'tone' | 'for' | 'own'): boolean;
  price(): number;
  missing(): Missing | null;
  ready(): string;
  missText(kind: string): string;
  label(): string | null;
  unit(): string;
  ytFile(): null;
  upOpts(tool: UpTool): { accept?: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[], at?: number): void };
  note(tool: UpTool): string;
  remove(tool: UpTool, i?: number): void;
  write(): void;
  tones(list: ToneCard[]): ToneCard[];
  items(list: SettingItem[]): SettingItem[];
  tpl(html: string): string;
  go(tool: string): void;
  gen(): void;
  varsNote(r: PromptRes): string | null;
  vrec(file: File | null): void;
  canRecord(): boolean;
  micText(kind: 'off' | 'denied'): string;
  recorded(x: { sec: number; blob: Blob | null; url: string | null }): void;
  camOff(tool: string): void;
  shot(file: File, tool: string): void;
  card(r: BotRunRun): string;
  plain(sn: Record<string, unknown>, plain?: boolean): string;
  script(sn: Record<string, unknown>): { text: string; speakers: string[] };
  steps(): string[][];
  /** Подпись страницы заказа «Можно уйти с экрана…» по виду заказа (целая строка словаря) */
  runP(r: BotRunRun): string;
  snap(): Record<string, unknown>;
  art(sn: Record<string, unknown>): { html: string; size: { w: number; h: number }; art?: boolean } | null;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  doneText(r: BotRunRun): string;
  start(btn: HTMLElement): void;
  leave(go: () => void, replace: boolean): void;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  improve(text: unknown, o?: { kind?: unknown; max?: unknown } | string): Promise<string[]>;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string; note(kind: string, text: string, acts?: string): string };
  frameSize(a: string, mw: number, mh: number): { w: number; h: number };
  files: { clip(file: File, cb: (c: Record<string, unknown>) => void, o?: Record<string, unknown>): void };
  prefs: { platform: { get(): string; set(v: string): boolean } };
  trim: { of(f: TextFile, max: number): { from: number; to: number } };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** accept рамок BotCore (их проверка типа — до моста): вид и расширения, у которых браузер не называет тип; точные типы и предел — у моста */
const ACCEPT: Record<'seo' | 'pphoto' | 'pvideo', string> = {
  seo: 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif',
  pphoto: 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif',
  pvideo: 'video/*,.mp4,.mov,.m4v,.webm',
};
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Отказ BotCore.upload по типу у рамки вида audio (KIND.audio.bad шаблона) */
const STT_BAD_AUDIO = 'Нужна запись: MP3, WAV или M4A';
/** Обложка ролика, пока кадр не прочитан (или не прочитался): прозрачная точка, а не <img src=""> — пустой адрес у картинки рамки
    BotCore.upload даёт ошибку загрузки; у шаблона ролик в рамку кладётся уже с обложкой (BotCore.files.clip) */
const NO_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
/** Столбиков волны записи в рамке и на странице заказа (BotCore.files.audio шаблона: 34) */
const WAVE_N = 34;
const flat = () => Array<number>(WAVE_N).fill(0.07);
const num = (value: unknown) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) && n > 0 ? n : undefined;
};
const mins = (sec: number | undefined) => Math.max(1, Math.ceil((sec || 0) / 60));
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
const orderClear = (id: number) => document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id } }));
/** Шаблонные значения S по инструментам (раздел 2 шаблона): у нового кадра состояние — с нуля */
const blank = (): Omit<TextState, 'tool'> => ({
  ytWant: false,
  long: false,
  v: 0,
  stt: { file: null, lang: 'auto', spk: true, tc: true },
  yt: { url: '', err: false, kind: 'full', lang: 'src' },
  seo: { files: [], text: '', name: '', feat: '', market: null, tone: 'sell', wish: '' },
  pphoto: { file: null, style: 'detail', lang: 'ru' },
  pvideo: { clip: null, style: 'detail', lang: 'ru' },
  pnew: { text: '', target: 'photo', tpl: [], own: false, ownText: '', res: null },
  pfix: { text: '', target: 'photo', tpl: [], own: false, ownText: '', res: null },
});
/** Причина родной формы (textMissing) словами профиля: шаблоны с числом — по своему шаблону */
function reasonText(reason: string): string {
  const n = /(\d+)/.exec(reason)?.[1];
  if (n && /^Текст длиннее /.test(reason)) return uiFormat('Текст длиннее {0} знаков', n);
  if (n && /^Напишите от /.test(reason)) return uiFormat('Напишите от {0} знаков', n);
  if (n && /^Пожелания длиннее /.test(reason)) return uiFormat('Пожелания длиннее {0} знаков', n);
  return uiText(reason);
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  driver: RunDriver;
  draft: TextDraft;
  /** Форма, для которой собраны списки */
  form: StudioForm | null;
  /** Инструмент кадра (по форме, до неё — по entry.tool приложения) */
  tool: TextTool;
  /** Раскладка формы на экране: опубликованные части */
  layout: string;
  said: string;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Варианты на месте этого кадра (T().res шаблона): живут в кадре и переживают уход к другому кадру */
  res: PromptRes | null;
  /** Файлы рамки по записи моста (тот же объект, пока запись та же): обложка, размеры, волна читаются после выбора */
  files: Map<string, TextFile>;
  /** Ключи, превью которых читается сейчас */
  restoring: Set<string>;
  /** Идёт уход к форме (правка после готовых вариантов): входы заказа не переносятся в S */
  leaving: boolean;
  /** Вход «Текст из YouTube» (tool yt): поле ссылки проявляется при открытии */
  ytWant: boolean;
  recN: number;
  consent: ConsentSheet | null;
  /** Документы итогов заказов кадра (text-result.ts) */
  results: TextResults;
  /** «Ещё варианты» вариантов на месте (text-more.ts) */
  more: TextMore;
  /** Что стоит в .tt-res итога (заказ, ревизия, документ или его состояние): тот же — не перерисовывается */
  docKey: string;
  /** Ошибка «Ещё варианты», уже показанная подсказкой */
  moreSaid: string;
}

/** Подпись итога по доставке: бот уже прислал — или пришлёт (T.doneP шаблона, родной хвост) */
const DONE_SENT: Record<string, string> = {
  stt: 'Бот уже прислал расшифровку в чат, в отдельную тему.',
  yt: 'Бот уже прислал текст в чат, в отдельную тему.',
  seo: 'Бот уже прислал описание в чат, в отдельную тему.',
  pphoto: 'Бот уже прислал промпт в чат, в отдельную тему.',
  pvideo: 'Бот уже прислал промпт в чат, в отдельную тему.',
};
const DONE_SOON: Record<string, string> = {
  stt: 'Бот пришлёт расшифровку в чат, в отдельную тему.',
  yt: 'Бот пришлёт текст в чат, в отдельную тему.',
  seo: 'Бот пришлёт описание в чат, в отдельную тему.',
  pphoto: 'Бот пришлёт промпт в чат, в отдельную тему.',
  pvideo: 'Бот пришлёт промпт в чат, в отдельную тему.',
};
/** Подпись страницы заказа: «Можно уйти с экрана: бот напишет, когда …» целой строкой по виду заказа */
const RUN_P: Record<string, string> = {
  stt: 'Можно уйти с экрана: бот напишет, когда расшифровка будет готова.',
  yt: 'Можно уйти с экрана: бот напишет, когда текст будет готов.',
  seo: 'Можно уйти с экрана: бот напишет, когда описание будет готово.',
  pphoto: 'Можно уйти с экрана: бот напишет, когда промпт будет готов.',
  pvideo: 'Можно уйти с экрана: бот напишет, когда промпт будет готов.',
};
/** Что бот присылает в чат (подсказка «Открыть в чате» вне Telegram) */
const CHAT_TIP: Record<string, string> = {
  stt: 'Тут откроется тема чата, куда бот прислал расшифровку',
  yt: 'Тут откроется тема чата, куда бот прислал текст',
  seo: 'Тут откроется тема чата, куда бот прислал описание',
  pphoto: 'Тут откроется тема чата, куда бот прислал промпт',
  pvideo: 'Тут откроется тема чата, куда бот прислал промпт',
};

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createTextServer(h: TextEngineHooks): TextServer {
  const { S } = h;
  /** Списки шаблона до первой формы: подписи значений и слова назначения */
  const base: TextListsBase = listsBase(h);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относится S — объект, а не ключ: ключи ServerStudio повторяются после нового входа в раздел */
  let painted: Frame | null = null;
  /** Кадр, из формы которого сейчас списки шаблона */
  let filled: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** S переносится из входов: запись S → входы (syncFoot) и подписчики площадки молчат */
  let reading = false;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const ups = () => h.ups();
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const formOf = (f: Frame | null) => f?.svc.get().form ?? null;
  const keysOf = (f: Frame | null) => textKeys(formOf(f));
  const policyOf = (f: Frame | null) => {
    const form = formOf(f);
    return form ? textPolicy(form) : null;
  };
  const toolNow = (f: Frame) => toolOf(formOf(f)) ?? toolOfEntry(f.app.entry) ?? 'stt';
  const inline = (f: Frame | null) => !!f && inlineTool(f.tool);
  /** Состояние инструмента кадра */
  const T = (f: Frame) => S[f.tool] as unknown as Record<string, unknown>;

  /* ---- строки шаблона на языке профиля: переводятся на месте из русских копий (демо-движок их не видит: без serve SRV нет) ---- */
  /* копии берутся при первом переводе: VERB и UNIT объявлены в движке ниже вставки serve (их нельзя читать, пока движок создаётся) */
  let RU: ReturnType<typeof russianCopies> | null = null;
  const russianCopies = () => ({
    TOOLS: h.TOOLS.map(tool => tool.slice()),
    R: JSON.parse(JSON.stringify(h.R)) as Record<string, Record<string, string>>,
    VERB: { ...h.VERB() },
    UNIT: { ...h.UNIT() },
    BOX: JSON.parse(JSON.stringify(h.BOX)) as TextEngineHooks['BOX'],
    IDEAS: h.IDEAS.map(idea => idea.slice()),
    FEATS: h.FEATS.slice(),
    SEO_WISH: h.SEO_WISH.map(wish => wish.slice()),
  });
  /** Русская подпись значения выбора (шаблон, иначе родная форма) — ключ словаря */
  const russianLabel = (list: string, value: string) => base.OPT[list]?.find(o => o[0] === value)?.[1] ?? choiceLabels[value] ?? value;
  function translate() {
    const ru = (RU ??= russianCopies());
    h.TOOLS.forEach((tool, i) => (tool[2] = uiText(ru.TOOLS[i]?.[2] ?? tool[2])));
    for (const [kind, texts] of Object.entries(ru.R)) for (const [key, value] of Object.entries(texts)) h.R[kind][key] = uiText(value);
    for (const [key, value] of Object.entries(ru.VERB)) h.VERB()[key] = uiText(value);
    for (const [key, value] of Object.entries(ru.UNIT)) h.UNIT()[key] = uiText(value);
    for (const [key, box] of Object.entries(ru.BOX)) Object.assign(h.BOX[key], { l: uiText(box.l), ph: uiText(box.ph), foot: uiText(box.foot) });
    ru.IDEAS.forEach(([label, text], i) => (h.IDEAS[i] = [uiText(label), uiText(text)]));
    ru.FEATS.forEach((label, i) => (h.FEATS[i] = uiText(label)));
    ru.SEO_WISH.forEach(([label, phrase], i) => (h.SEO_WISH[i] = [uiText(label), uiText(phrase)]));
    for (const [list, items] of Object.entries(h.OPT)) for (const o of items) o[1] = uiText(russianLabel(list, o[0]));
    for (const f of h.FOR) {
      const ru = base.FOR.find(item => item[0] === f[0]);
      f[1] = uiText(ru?.[1] ?? choiceLabels[f[0]] ?? f[0]);
      f[2] = uiText(ru?.[2] ?? (choiceLabels[f[0]] ?? f[0]).toLowerCase());
    }
    /* шаблоны промпта: ключ — подпись сервера (уходит в заказ), показ — словарём профиля */
    for (const rows of Object.values(h.TPL)) for (const list of Object.values(rows)) for (const row of list) row[1] = uiText(row[0]);
  }

  /* «Улучшить» вида text (пожелания описания товара): POST /app/text/improve, только если форма кадра публикует improve_available */
  setTextImprove(() => policyOf(cur() ?? frame)?.improve_available === true);

  /* ---- списки шаблона из формы ---- */
  function fillLists(f: Frame) {
    filled = f;
    const form = formOf(f);
    if (!form) return;
    fillTextLists(form, h, base);
    translate();
    /* предел записи с микрофона — поле звука формы (max_duration_ms) */
    const audio = keysOf(f).audio;
    const max = audio ? num(toolField(form, audio)?.upload?.max_duration_ms) : undefined;
    if (max) h.setRecMax(Math.round(max / 1000));
    /* предел веса рамки — поля формы (рамка BotCore проверяет его до моста) */
    const keys = keysOf(f);
    const mb = (key: string | undefined) => {
      const upload = key ? toolField(form, key)?.upload : null;
      return upload?.max_bytes ? upload.max_bytes / MB : undefined;
    };
    const U = ups();
    const sttMb = Math.max(mb(keys.audio) ?? 0, mb(keys.video) ?? 0);
    if (sttMb) U.stt.o.mb = sttMb;
    if (mb(keys.photos[0])) U.seo.o.mb = U.pphoto.o.mb = mb(keys.photos[0]);
    if (mb(keys.video)) U.pvideo.o.mb = mb(keys.video);
    /* лоток описания: столько фото, сколько полей photo… публикует форма (не больше photo_max_count) */
    const tray = trayKeys(f);
    if (tray.length) U.seo.o.multi = tray.length;
  }

  /* ---- ключи файлов ---- */
  /** Слоты лотка фото товара: поля photo, photo_2… формы, не больше photo_max_count политики */
  function trayKeys(f: Frame): string[] {
    const photos = keysOf(f).photos;
    const max = num(policyOf(f)?.photo_max_count);
    return max ? photos.slice(0, Math.max(1, Math.floor(max))) : photos;
  }
  /** Ключ одиночной рамки инструмента: фото (pphoto), видео (pvideo); у расшифровки — звук или видео по входам */
  function singleKey(f: Frame, tool: UpTool): string | undefined {
    const keys = keysOf(f);
    if (tool === 'pphoto') return keys.photos[0];
    if (tool === 'pvideo') return keys.video;
    if (tool === 'stt') {
      const inputs = f.svc.get().inputs;
      if (keys.video && (inputs[keys.video] || f.bridge.item(keys.video))) return keys.video;
      return keys.audio ?? keys.video;
    }
    return undefined;
  }
  /** Ключи источника расшифровки: звук и видео формы */
  const sttKeys = (f: Frame) => [keysOf(f).audio, keysOf(f).video].filter((key): key is string => !!key);
  function mediaPolicyOf(f: Frame, key: string): MediaPolicy | null {
    const form = formOf(f);
    const policy = policyOf(f);
    if (!form || !policy) return null;
    const p = mediaPolicy(form, key);
    if (!p) return null;
    /* ролик промпта: длиннее предела — выбирается фрагмент (общая обрезка), поэтому предела длины у моста нет */
    if (p.kind === 'video') return { ...p, maxMs: null, minMs: null };
    return p;
  }

  /* ---- файлы рамок (мост → S) ---- */
  function visual(f: Frame, item: MediaItem): TextFile {
    let file = f.files.get(item.id);
    const ms = item.meta.durationMs;
    if (!file) {
      file = {
        name: item.rec ? uiFormat('Запись {0}', ++f.recN) : item.restored ? uiText(item.kind === 'audio' ? 'Запись' : item.kind === 'video' ? 'Видео' : 'Фото') : item.name,
        size: item.size,
        url: '',
        kind: item.kind,
        mid: item.id,
        ...(item.rec ? { rec: true } : {}),
      };
      f.files.set(item.id, file);
    }
    file.size = item.size;
    if (item.kind === 'audio') {
      file.url = item.url;
      if (ms && ms > 0) file.dur = Math.round(ms / 1000);
      if (!file.peaks) file.peaks = flat();
    } else if (item.kind === 'photo') {
      file.url = item.url;
      if (item.meta.width && item.meta.height) {
        file.w = item.meta.width;
        file.h = item.meta.height;
      }
      if (!file.w || !file.h) {
        file.w = 540;
        file.h = 720;
      }
    } else {
      file.url = '';
      if (item.url) file.video = item.url;
      if (ms && ms > 0 && !file.dur) file.dur = ms / 1000;
      file.poster ||= NO_POSTER;
    }
    return file;
  }
  /** Волна и длина записи из самого файла (WebAudio); размеры фото — из картинки; обложка и длина ролика — из кадра (BotCore.files.clip) */
  function readMeta(f: Frame, item: MediaItem, file: TextFile) {
    const blob = item.file;
    if (!blob) return;
    const paint = () => {
      if (cur() === f && onForm()) {
        h.paintUp();
        if (f.tool === 'pvideo') h.trimPaint();
      }
    };
    if (item.kind === 'audio')
      void filePeaks(blob, WAVE_N)
        .then(result => {
          if (!result || f.files.get(item.id) !== file) return;
          file.peaks = result.peaks;
          if (!file.dur && result.durationMs > 0) file.dur = Math.round(result.durationMs / 1000);
          paint();
        })
        .catch(() => {});
    else if (item.kind === 'photo' && item.url && !item.meta.width) {
      const image = new Image();
      image.onload = () => {
        if (f.files.get(item.id) !== file || !image.naturalWidth) return;
        file.w = image.naturalWidth;
        file.h = image.naturalHeight;
        paint();
      };
      image.src = item.url;
    } else if (item.kind === 'video' && item.url) {
      /* длина — метаданные ролика (мост её не читал: предела длины у ролика нет, фрагмент выбирается обрезкой) */
      if (!item.meta.durationMs)
        void mediaFile.readDuration(blob as File, 'video').then(ms => {
          if (!ms || f.files.get(item.id) !== file || file.dur) return;
          file.dur = ms / 1000;
          trimFrom(f, file);
          paint();
        });
      clipMeta(f, item, file, blob as File);
    }
  }
  /** Обложка, размеры и длина ролика — кадр из начала (BotCore.files.clip) */
  function clipMeta(f: Frame, item: MediaItem, file: TextFile, blob: File) {
    core().files.clip(
      blob,
      c => {
        if (f.files.get(item.id) !== file) return;
        if (typeof c.poster === 'string') file.poster = c.poster;
        if (num(c.w) && num(c.h)) {
          file.w = c.w as number;
          file.h = c.h as number;
        }
        if (num(c.dur)) file.dur = c.dur as number;
        trimFrom(f, file);
        if (cur() === f && onForm()) {
          h.paintUp();
          if (f.tool === 'pvideo') h.trimPaint();
        }
      },
      { url: item.url },
    );
  }
  /** Окно обрезки ролика — начало из входов (from_ms), длина — предел шаблона */
  function trimFrom(f: Frame, file: TextFile) {
    if (f.tool !== 'pvideo' || !file.dur) return;
    const form = formOf(f);
    const view = form ? readText(form, f.svc.get().inputs) : null;
    const max = h.clipMax();
    const from = view?.tool === 'pvideo' ? Math.max(0, Math.min(view.v.from, Math.max(0, file.dur - max))) : 0;
    file.trim = { from, to: Math.min(file.dur, from + max) };
  }
  /** Файлы кадра из моста → S инструмента; отрисовка — по месту (рамка проявляется, блоки ниже едут) */
  function paintFiles(f: Frame, repaint = true) {
    if (cur() !== f) return;
    const before = JSON.stringify(fileKeys());
    switch (f.tool) {
      case 'stt': {
        const item = sttKeys(f).map(key => f.bridge.item(key)).find(Boolean) ?? null;
        S.stt.file = item ? visual(f, item) : null;
        break;
      }
      case 'seo':
        S.seo.files = f.bridge.slots(trayKeys(f)).list().map(item => visual(f, item));
        break;
      case 'pphoto': {
        const key = singleKey(f, 'pphoto');
        const item = key ? f.bridge.item(key) : null;
        S.pphoto.file = item ? visual(f, item) : null;
        break;
      }
      case 'pvideo': {
        const key = singleKey(f, 'pvideo');
        const item = key ? f.bridge.item(key) : null;
        S.pvideo.clip = item ? visual(f, item) : null;
        if (S.pvideo.clip) trimFrom(f, S.pvideo.clip);
        break;
      }
      default:
        break;
    }
    if (repaint && onForm() && JSON.stringify(fileKeys()) !== before) {
      if (f.tool === 'seo') {
        h.glide(() => {
          ups().seo.paint();
          h.fitSync();
        });
        h.syncFoot();
      } else h.paintUp();
    }
  }
  /** Что в рамках сейчас (для сравнения: записи и их адреса) */
  const fileKeys = () => [S.stt.file, S.pphoto.file, S.pvideo.clip, ...S.seo.files].map(file => (file ? `${file.mid}|${file.url}|${file.video ?? ''}` : ''));
  /** Файлы во входах — те, что в рамках: загрузка повтора — превью сервера; входы сменились не формой — запись рамки снимается */
  function filesSync(f: Frame) {
    const s = f.svc.get();
    const form = s.form;
    if (!form || !s.identity) return;
    const locked = s.busy || f.svc.derive().locked;
    if (f.tool === 'seo') {
      const keys = trayKeys(f);
      if (!keys.length) return;
      const tray = f.bridge.slots(keys);
      const want = keys.map(key => s.inputs[key] ?? '').filter(Boolean);
      const have = tray.list().filter(item => item.uploadId && (item.state === 'ready' || item.state === 'restoring')).map(item => item.uploadId);
      const pending = tray.list().some(item => item.state !== 'ready' && item.state !== 'restoring');
      if (!pending && !locked && !f.restoring.has('tray') && want.join() !== have.join()) {
        f.restoring.add('tray');
        void tray.restore(want).finally(() => {
          f.restoring.delete('tray');
          if (cur() === f) {
            paintFiles(f);
            changed(f);
          }
        });
      }
      return;
    }
    const keys = f.tool === 'stt' ? sttKeys(f) : ([singleKey(f, f.tool as UpTool)].filter(Boolean) as string[]);
    for (const key of keys) {
      const id = s.inputs[key];
      const item = f.bridge.item(key);
      if (item && item.state === 'ready' && item.uploadId !== id && !locked) {
        /* входы сменились не формой (повтор, «Изменить»): прежний файл рамки — уже не файл заказа */
        f.bridge.remove(key);
        f.files.delete(item.id);
      }
      if (id && !f.bridge.item(key) && !f.restoring.has(key)) {
        f.restoring.add(key);
        void f.bridge.restore(key, id).finally(() => {
          f.restoring.delete(key);
          if (cur() === f) {
            paintFiles(f);
            changed(f);
          }
        });
      }
    }
  }

  /* ---- входы ↔ S ---- */
  /** Вид экрана из S (то, что форма пишет во входы) */
  function viewOf(f: Frame): TextView {
    switch (f.tool) {
      case 'stt':
        return { tool: 'stt', v: { lang: S.stt.lang, spk: S.stt.spk, tc: S.stt.tc, url: S.yt.url, ytKind: S.yt.kind, ytLang: S.yt.lang } };
      case 'seo':
        return { tool: 'seo', v: { text: S.seo.text, tone: S.seo.tone, market: h.seoMarket(), wish: S.seo.wish } };
      case 'pphoto':
      case 'pvideo': {
        const t = S[f.tool];
        const from = f.tool === 'pvideo' && S.pvideo.clip?.trim ? S.pvideo.clip.trim.from : 0;
        return { tool: f.tool, v: { style: t.style, lang: t.lang, from } };
      }
      default: {
        const t = S[f.tool];
        return { tool: f.tool, v: { text: t.text, target: t.target, tpl: t.tpl.slice(), own: t.own, ownText: t.ownText } };
      }
    }
  }
  /** Вид входов → S; true — S изменилось */
  function apply(f: Frame, view: TextView): boolean {
    const before = JSON.stringify(S[f.tool === 'stt' ? 'stt' : f.tool]) + JSON.stringify(S.yt);
    reading = true;
    try {
      switch (view.tool) {
        case 'stt': {
          const v = view.v;
          Object.assign(S.stt, { lang: v.lang, spk: v.spk, tc: v.tc });
          Object.assign(S.yt, { kind: v.ytKind, lang: v.ytLang });
          if (v.url) {
            S.yt.url = v.url;
            S.yt.err = false;
          } else if (h.ytId(S.yt.url)) {
            /* верной ссылки во входах больше нет: её сняли не формой */
            S.yt.url = '';
            S.yt.err = false;
          }
          break;
        }
        case 'seo': {
          const v = view.v;
          S.seo.text = v.text;
          h.seoParse();
          S.seo.tone = v.tone;
          S.seo.wish = v.wish;
          marketIn(f, v.market);
          break;
        }
        case 'pphoto':
        case 'pvideo': {
          const t = S[view.tool];
          t.style = view.v.style;
          t.lang = view.v.lang;
          if (view.tool === 'pvideo' && S.pvideo.clip) trimFrom(f, S.pvideo.clip);
          break;
        }
        default: {
          const t = S[view.tool];
          const v = view.v as PromptView;
          Object.assign(t, { text: v.text, target: v.target, tpl: v.tpl.slice(), own: v.own, ownText: v.ownText });
          break;
        }
      }
    } finally {
      reading = false;
    }
    return before !== JSON.stringify(S[f.tool === 'stt' ? 'stt' : f.tool]) + JSON.stringify(S.yt);
  }
  /** Площадка из входов: своя описания (Avito, «Свой сайт») — у экрана; общая товарная (wb · ozon · ym) — общий выбор BotCore.prefs.platform.
      Значение формы по умолчанию общий выбор не перебивает (шаблон: площадка одна на все товарные экраны), другое — становится им */
  function marketIn(f: Frame, value: string) {
    if (!value) return;
    if (!h.isPlat(value)) {
      S.seo.market = value;
      return;
    }
    S.seo.market = null;
    const field = toolField(formOf(f), keysOf(f).market ?? '');
    const prefs = core().prefs.platform;
    if (value !== String(field?.default_value ?? '') && prefs.get() !== value) prefs.set(value);
  }
  /** Запись входов формой (зеркало: под замком сервиса — нет) */
  function commit(f: Frame, next: (prev: Record<string, string>) => Record<string, string>): boolean {
    const { s, d } = live(f);
    if (!d.locked) return f.mirror.commit(next);
    if (s.intent || d.submissionBlocked || !s.uploadingFile) return false;
    let wrote: Record<string, string> | null = null;
    f.svc.setters.inputs(current => {
      const value = next(current);
      if (sameInputs(value, current)) return current;
      wrote = value;
      return value;
    });
    if (wrote) f.mirror.seen(wrote);
    return !!wrote;
  }
  /** S → входы кадра */
  function writeView(f: Frame, view = viewOf(f)): boolean {
    const form = formOf(f);
    if (!form || f.form !== form) return false;
    return commit(f, prev => {
      const next = writeText(form, view, prev);
      return f.tool === 'pvideo' && S.pvideo.clip?.trim ? writeFrom(form, S.pvideo.clip.trim.from, next) : next;
    });
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  const layoutOf = (f: Frame) =>
    [f.tool, server.has('url'), server.has('tray'), server.has('tone'), server.has('for'), server.has('own'), JSON.stringify(h.OPT), JSON.stringify(h.FOR), trayKeys(f).length].join('|');
  /** Форма и входы → списки шаблона и S (без отрисовки); true — форму надо нарисовать заново */
  function syncState(f: Frame): boolean {
    const { s } = live(f);
    const form = s.form;
    let repaint = false;
    if (form && (f.form !== form || filled !== f)) {
      f.form = form;
      const tool = toolNow(f);
      if (tool !== f.tool) {
        f.tool = tool;
        S.tool = tool;
        repaint = true;
      }
      fillLists(f);
    }
    if (form && !f.leaving && f.mirror.external(s.inputs)) {
      const view = readText(form, s.inputs);
      if (view && apply(f, view)) repaint = true;
      f.mirror.seen(s.inputs);
    }
    if (form) filesSync(f);
    paintFiles(f, false);
    const layout = layoutOf(f);
    if (layout !== f.layout) {
      f.layout = layout;
      repaint = true;
    }
    return repaint;
  }
  function formSync(f: Frame) {
    const repaint = syncState(f);
    if (!onForm()) return;
    if (repaint) h.paintPage();
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const now = f.svc.get();
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      const U = upFrame(f);
      if (FORM_SAY.includes(error) && U) U.say(uiText(error));
    }
    h.syncFoot();
  }
  const upFrame = (f: Frame): EngineUpload | null => (f.tool === 'pnew' || f.tool === 'pfix' ? null : ups()[f.tool]);
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    f.draft.sync();
    formSync(f);
    if (inline(f)) inlineSync(f);
    else f.driver.sync();
    f.bridge.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.syncFoot();
      });
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  /** Права на материалы после перезагрузки (файлы повтора или черновика, а согласия в этой вкладке ещё не было): лист подтверждения */
  function needsRights(f: Frame) {
    const { s } = live(f);
    const form = s.form;
    return !!form && !s.rights && Object.keys(s.inputs).some(key => !!s.inputs[key] && !!toolField(form, key)?.upload);
  }
  function confirmRights(f: Frame, then: () => void) {
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      onContinue: () => {
        f.consent = null;
        if (cur() === f) then();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- вход заказа: тело заявки (или подсмотренной), иначе форма ---- */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Входы тела заявки строками, как входы формы */
  const bodyInputs = (it: Intent) =>
    Object.fromEntries(Object.entries(it.body.inputs as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]));
  /** Входы заказа: тело заявки, иначе входы формы */
  const sentInputs = (f: Frame | null) => {
    const it = intentOf(f);
    return it ? bodyInputs(it) : (f?.svc.get().inputs ?? {});
  };
  /** Вид заказа (kindNow шаблона) по входам заказа: у расшифровки — файл или ссылка */
  function sentKind(f: Frame | null): TextKind {
    const policy = policyOf(f);
    if (!f || !policy) return (f?.tool ?? 'stt') as TextKind;
    return textKind(policy, sentInputs(f), keysOf(f));
  }

  /* ---- варианты на месте («Создать промпт», «Улучшить промпт») ---- */
  /** Снимок ввода варианта (gSnap шаблона) по входам заказа */
  function promptSnap(f: Frame, inputs: Record<string, string>): { snap: PromptRes['snap']; sig: string } {
    const form = formOf(f);
    const view = form ? readText(form, inputs) : null;
    const v: PromptView =
      view && (view.tool === 'pnew' || view.tool === 'pfix') ? view.v : { text: '', target: 'photo', tpl: [], own: false, ownText: '' };
    const snap = { tool: f.tool, target: v.target, tpl: v.tpl.slice(), own: v.own ? v.ownText.trim() : '', text: v.text.trim(), n: 0 };
    return { snap, sig: h.sigOf(v) };
  }
  /** Политика «Ещё варианты» формы (constraints.text.more) */
  const morePolicy = (f: Frame) => record(policyOf(f)?.more);
  const moreReady = (f: Frame) => {
    const p = morePolicy(f);
    return p.available === true && typeof p.group_key === 'string' && !!p.group_key && typeof p.expected_price === 'number' && p.expected_price >= 0;
  };
  /** Заказ вариантов по id: корень (заказ сервиса) или группа «Ещё варианты» */
  function orderById(f: Frame, id: number | undefined) {
    const order = f.svc.get().order;
    if (!id) return null;
    if (order?.id === id) return order;
    return f.more.state().orders.find(item => item.id === id) ?? null;
  }
  /** Сверка вариантов на месте с сервисом заказа кадра */
  function inlineSync(f: Frame) {
    if (cur() !== f || f.leaving) return;
    const { s, d } = live(f);
    const phase = phaseOf(s, d);
    if (phase === 'load') return;
    const prev = f.res;
    let next: PromptRes | null = prev;
    /* «Ещё варианты» — цепочка готового заказа */
    f.more.load(s.identity?.owner, phase === 'done' ? s.order : null, morePolicy(f));
    if (phase === 'form') {
      /* заказа больше нет: идущие варианты без заказа (заявку сняли, отказ до создания) — заметкой отказа или ничем */
      if (prev && (prev.st === 'run' || prev.st === 'unk')) next = s.error ? { ...prev, st: 'rej', error: s.error } : null;
    } else {
      const it = intentOf(f);
      const { snap, sig } = promptSnap(f, it ? bodyInputs(it) : s.inputs);
      const id = d.orderId;
      const st: PromptRes['st'] = phase === 'run' ? 'run' : phase === 'done' ? 'run' : phase === 'mod' ? 'mod' : phase === 'tech' ? 'fail' : 'unk';
      next = { st, sig, snap, price: d.price, test: null, ...(id ? { id } : {}) };
      if (phase === 'done') Object.assign(next, doneVars(f, snap));
    }
    const more = f.more.state();
    if (more.error && more.error !== f.moreSaid) {
      f.moreSaid = more.error;
      tip(null, more.error);
    } else if (!more.error) f.moreSaid = '';
    if (JSON.stringify(next) === JSON.stringify(prev)) return;
    f.res = next;
    T(f).res = next;
    if (onForm()) {
      h.paintVars(!!next && (!prev || prev.st !== next.st || prev.id !== next.id));
      h.footRedo();
    }
  }
  /** Варианты готового заказа: последняя группа (корень или «Ещё варианты», Q-T2), её документ; пока он читается или группа пишется —
      «Пишем варианты»; группа не прошла проверку или сбой — заметка с суммами этой группы */
  function doneVars(f: Frame, snap: PromptRes['snap']): Partial<PromptRes> {
    const root = f.svc.get().order;
    const more = f.more.state();
    const groups = [root, ...more.orders].filter((item): item is NonNullable<typeof item> => !!item);
    const latest = groups.at(-1);
    const n = Math.max(0, groups.length - 1);
    /* группа пишется (POST в полёте, дочерний заказ идёт) — «Пишем варианты»; неизвестный POST без заказа ждёт «Продолжить проверку»: на месте прежняя группа */
    if (!latest || more.busy || more.pending?.child) return { st: 'run', snap: { ...snap, n: n + 1 } };
    const ofGroup = { id: latest.id, snap: { ...snap, n } };
    if (latest.status === 'failed') return { st: tailError(latest)?.kind === 'moderation' ? 'mod' : 'fail', ...ofGroup };
    if (latest.status !== 'done') return { st: 'run', ...ofGroup };
    const result = f.results.get(latest);
    if (result.doc) return { st: 'done', vars: variantsOf(result.doc), ...ofGroup };
    if (result.error) return { st: 'err', ...ofGroup };
    return { st: 'run', ...ofGroup };
  }
  /** Уход к форме с вариантами на месте (первая правка после готовых вариантов, новый заказ того же ввода): заказ закрывается, как у
      родной формы (returnToForm, просмотрено, order-clear); варианты остаются на месте */
  async function inlineLeave(f: Frame): Promise<boolean> {
    const id = f.svc.derive().orderId;
    if (!id) return true;
    f.leaving = true;
    let ok: true | undefined;
    try {
      ok = await f.svc.actions.returnToForm();
    } finally {
      f.leaving = false;
    }
    if (!ok) {
      if (cur() === f) tip(null, f.svc.get().error);
      return false;
    }
    markSeen(id);
    orderClear(id);
    return true;
  }
  /** «Создать промпт» / «Улучшить промпт»: заказ через сервис; варианты встают по его ходу (inlineSync) */
  async function inlineCreate(f: Frame) {
    const { s, d } = live(f);
    if (f.res?.st === 'run' && (d.orderId || s.busy)) return;
    if (d.orderId) {
      const status = s.order?.status;
      /* исход не подтверждён (uncertain): только GET того же заказа, нового не создаём */
      if (status === 'uncertain') {
        void f.svc.actions.refreshStatus();
        return;
      }
      if (status !== 'done' && status !== 'failed') return;
      /* тот же ввод после готовых вариантов: «Ещё варианты» (отдельный заказ группы, ключ — до POST, text-more.ts) */
      const more = f.more.state();
      if (status === 'done' && f.res?.sig === h.sigOf(S[f.tool as 'pnew' | 'pfix']) && (more.pending || moreReady(f))) {
        if (more.blocked) return tip(null, 'Проверяем доступность');
        if (more.busy) return;
        void (more.pending ? f.more.retry() : f.more.request());
        if (f.res && !more.pending) {
          f.res = { ...f.res, st: 'run', snap: { ...f.res.snap, n: f.res.snap.n + 1 } };
          T(f).res = f.res;
          h.paintVars(true);
          h.footRedo();
          h.varsInView();
        }
        return;
      }
      /* другой ввод или ввод после сбоя (или «Ещё варианты» форма не публикует): прежний заказ закрывается, новый — с новым ключом */
      const want = viewOf(f);
      if (!(await inlineLeave(f))) return;
      if (cur() !== f) return;
      writeView(f, want);
    }
    const it = intentOf(f);
    const snapIn = it ? bodyInputs(it) : f.svc.get().inputs;
    const { snap, sig } = promptSnap(f, snapIn);
    f.res = { st: 'run', sig, snap, price: f.svc.derive().price, test: null };
    T(f).res = f.res;
    h.paintVars(true);
    h.footRedo();
    h.varsInView();
    await f.svc.actions.submit({});
    if (cur() !== f) return;
    const outcome = f.svc.outcome;
    if ((outcome === 'dropped' || outcome === 'blocked') && !f.svc.derive().orderId && !(outcome === 'blocked' && f.svc.get().intent)) {
      f.res = { ...f.res, st: 'rej', error: f.svc.get().integrityError || f.svc.get().error };
      T(f).res = f.res;
      if (onForm()) {
        h.paintVars(true);
        h.footRedo();
      }
      return;
    }
    changed(f);
  }

  /* ---- итог: вид для чтения ---- */
  /** Документ итога заказа кадра (null — читается, не вышел или заказа нет) */
  const docOf = (f: Frame): TextDocument | null => f.results.get(f.svc.get().order).doc;
  const str = (value: unknown) => (typeof value === 'string' ? value : undefined);
  function readingCtx(r: BotRunRun): ReadingContext {
    const sn = r.snap as Record<string, unknown>;
    const file = (sn.file ?? sn.clip) as TextFile | null | undefined;
    return {
      kind: String(sn.tool),
      spk: sn.spk !== false,
      tc: sn.tc !== false,
      lang: str(sn.lang),
      ytKind: str(sn.kind),
      market: str(sn.market),
      tone: str(sn.tone),
      style: str(sn.style),
      plang: str(sn.lang),
      file: file ? { name: file.name, dur: file.dur } : null,
      open: !!(r as BotRunRun & { open?: boolean }).open,
    };
  }
  const kit = () => ({
    esc: h.esc,
    rhHTML: h.rhHTML,
    parasHTML: h.parasHTML,
    ulHTML: h.ulHTML,
    cp: h.cp,
    option: (list: string, value: string) => uiText(h.OPT[list]?.find(o => o[0] === value)?.[1] ?? value),
    tone: (id: string) => uiText(h.TONES.find(t => t.id === id)?.n ?? id),
  });
  /** Содержимое .tt-res итога: документ сервера видом для чтения; читается — «Загружаем…»; не открылся — причина и «Повторить» */
  function cardInner(f: Frame, r: BotRunRun): string {
    h.cpReset();
    const order = f.svc.get().order;
    if (!order || order.status !== 'done' || !order.result?.available) return '';
    const { doc, error } = f.results.get(order);
    if (doc) return readingHTML(doc, readingCtx(r), kit());
    if (error)
      return `<p class="hintp" role="alert">${esc(uiText('Не удалось открыть результат.'))} <button type="button" class="lnk" data-tt-reload>${esc(uiText('Повторить'))}</button></p>`;
    return `<p class="hintp" role="status">${esc(uiText('Загружаем…'))}</p>`;
  }
  /** Заказ готов (каждая сверка): документ итога встаёт в .tt-res на месте (как copyAct шаблона: .tt-res под текстом заказа, без id) (якоря страницы заказа не пересоздаются) */
  function onDone(f: Frame) {
    const r = h.tail.run;
    const order = f.svc.get().order;
    if (!r || r.st !== 'done' || !order) return;
    const { doc, error } = f.results.get(order);
    const key = `${order.id}:${order.result_revision}:${doc ? 'doc' : error ? 'err' : 'load'}:${(r as BotRunRun & { open?: boolean }).open ? 1 : 0}`;
    if (key === f.docKey) return;
    f.docKey = key;
    const el = h.root()?.querySelector('.ar-more .tt-res');
    if (!el) return;
    const html = cardInner(f, r);
    if (el.innerHTML !== html) el.innerHTML = html;
  }
  /** Название и описание товара одной строкой для «Видео товара» (родной хвост: productLine) */
  const productLine = (doc: TextDocument) =>
    doc.blocks
      .filter(block => block.label === 'Заголовок' || block.label === 'Описание')
      .map(block => block.text)
      .join('\n') || textDocumentPlain(doc);
  /** «Что дальше» итога: другие экраны студии с текстом итога (родной хвост) */
  function nextAction(d: DOMStringMap, el: HTMLElement, doc: TextDocument | null): boolean {
    const want = d.ttTophoto !== undefined || d.ttTovideo !== undefined || d.ttToseo !== undefined || d.ttTotts !== undefined || d.ttTocover !== undefined;
    if (!want) return false;
    if (!doc) {
      tip(el, 'Загружаем…');
      return true;
    }
    const plain = textDocumentPlain(doc);
    if (d.ttTophoto !== undefined) openStudioTarget({ screen: 'photo', options: { mode: 'generate', description: clipText(plain, PROMPT_MAX) } });
    else if (d.ttTovideo !== undefined) openStudioTarget({ screen: 'video', options: { mode: 'text', description: clipText(plain, PROMPT_MAX) } });
    else if (d.ttToseo !== undefined) openStudioTarget({ screen: 'text', options: { mode: 'product_description', text: Array.from(plain).slice(0, 1120).join('') } });
    else if (d.ttTotts !== undefined) openProjectsWith({ ...scriptOf(doc), name: uiText('Расшифровка') });
    /* «Сделать видео товара»: описание, без фото товара (Q-T4, как родной хвост) */
    else openStudioTarget({ screen: 'video', options: { mode: 'product', description: clipText(productLine(doc), PROMPT_MAX) } });
    return true;
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const baseMissing = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot((opts.foot as Record<string, unknown>) ?? {}, 'tt'), 'tt'),
      T: { ...((opts.T as Record<string, unknown>) ?? {}), doneP: (r: BotRunRun) => server.doneText(r) },
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return baseMissing();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        ((inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key])(...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /** «Назад» внутри экрана (Telegram, «назад» раздела): сначала верхний слой — «Улучшить», поле во весь экран, лист ядра */
  function back(f: Frame): boolean {
    if (cur() !== f || !h.mounted()) return false;
    const bc = window.BotCore as unknown as { improve?: { isOpen: boolean; close(): void }; editor?: { isOpen: boolean; close(): void } };
    if (bc.improve?.isOpen) {
      bc.improve.close();
      return true;
    }
    if (bc.editor?.isOpen) {
      bc.editor.close();
      return true;
    }
    const sheets = [...document.querySelectorAll<HTMLElement>('.bsheet.open[aria-hidden="false"]')];
    const top = sheets.at(-1);
    const x = top?.querySelector<HTMLElement>('[data-bsheet-x],.xbtn');
    if (!x) return false;
    x.click();
    return true;
  }

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.draft.stop();
        frame.results.stop();
        frame.more.stop();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      back: () => back(mine),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    const entry = b.initialInputs ?? {};
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      tool: toolOfEntry(b.app.entry) ?? 'stt',
      layout: '',
      said: '',
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      res: null,
      files: new Map(),
      restoring: new Set(),
      leaving: false,
      ytWant: entry.tool === 'yt' || (b.app.entry as Record<string, unknown> | undefined)?.tool === 'yt',
      recN: 0,
      consent: null,
      docKey: '',
      moreSaid: '',
    });
    f.results = createTextResults({ owner: () => f.svc.get().identity?.owner, changed: () => changed(f) });
    f.more = createTextMore({ changed: () => changed(f) });
    const say = (text: string, key: string) => {
      const keys = keysOf(f);
      const U = ups();
      const at = keys.photos.includes(key) ? (f.tool === 'seo' ? U.seo : U.pphoto) : key === keys.video && f.tool === 'pvideo' ? U.pvideo : U.stt;
      at.say(text);
    };
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard(say),
      policy: key => mediaPolicyOf(f, key),
      put: guard((item: MediaItem) => {
        const file = visual(f, item);
        paintFiles(f);
        readMeta(f, item, file);
        if (onForm() && !item.restored) h.ui().buzz();
      }),
      drop: guard((item: MediaItem | null) => {
        if (item) f.files.delete(item.id);
        paintFiles(f);
      }),
      update: guard(() => {
        paintFiles(f);
        if (cur() === f && onForm()) h.syncFoot();
      }),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
      /* «В записи не нашлось речи» (stt_no_speech) — отказ по входу, а не сбой сервиса: страница mod с «Заменить файл» (родной TextTail) */
      failKind: order => (textTailFail(order) ? 'mod' : null),
    });
    f.draft = createTextDraft({ svc: b.svc, seeded: !!b.initialInputs, changed: () => changed(f) });
    return f;
  }
  /** Новый кадр на экране: S с нуля (шаблонные значения), инструмент кадра, его варианты на месте */
  function resetState(f: Frame) {
    h.liveStop();
    h.sl.close();
    Object.assign(S, blank(), { tool: f.tool, ytWant: f.ytWant });
    S.pnew.res = f.tool === 'pnew' ? f.res : null;
    S.pfix.res = f.tool === 'pfix' ? f.res : null;
  }

  /** Цена за минуту распознавания (app.price, unit per_minute) */
  const perMinute = (f: Frame) => {
    const price = toolPrice(f.app.price);
    return price.unit === 'per_minute' && price.amount !== null ? price.amount : null;
  };
  /** Расчёт сервера (quote_endpoint) для нынешних входов уже есть: цена кнопки — она */
  const quoted = (f: Frame) => {
    const { s, d } = live(f);
    return !!d.quoteEndpoint && !!s.quote && s.quote.key === d.quoteBody;
  };

  const server: TextServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          if (!f || recovery(f) || f.draft.blocked()) return Infinity;
          const s = f.svc.get();
          /* расшифровка без длины файла: сумма ещё неизвестна — «Не хватает» не показывается */
          const known = f.tool !== 'stt' || h.srcOf() !== 'file' || !!S.stt.file?.dur || quoted(f);
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, known);
        },
      }),
    open() {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: файлы, выборы, варианты и хвост прежнего сюда не переходят */
        f.tool = toolNow(f);
        resetState(f);
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
      }
      S.tool = f.tool;
      f.formShown = false;
      translate();
      const root = h.root();
      if (!inline(f) && h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      /* входы → S до первой отрисовки: подвал формы пишет S обратно во входы */
      syncState(f);
      if (inline(f)) {
        h.paintPage();
        if (root) root.scrollTop = 0;
      } else if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      if (f.ytWant && f.tool === 'stt' && !S.stt.file) {
        const field = document.getElementById('ttLinkF');
        if (field) window.SteosUI?.pop?.(field);
      }
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    has(k) {
      const f = cur() ?? frame;
      const form = formOf(f);
      if (!f || !form) return true;
      const keys = keysOf(f);
      switch (k) {
        case 'url':
          return !!keys.url;
        case 'tray':
          return trayKeys(f).length > 0;
        case 'tone':
          return !!keys.tone && !!toolField(form, keys.tone)?.values.length;
        case 'for':
          return !!keys.kind && !!toolField(form, keys.kind)?.values.length;
        default:
          return !!keys.wishes;
      }
    },
    price() {
      const f = cur() ?? frame;
      if (!f) return 0;
      const { s, d } = live(f);
      if (!s.form) return toolPrice(f.app.price).amount ?? 0;
      if (quoted(f) || s.intent) return d.price;
      const kind = h.kindNow();
      const rate = perMinute(f);
      if (kind === 'stt' && rate !== null) return rate * mins(S.stt.file?.dur);
      if (kind === 'yt') {
        const yt = num(policyOf(f)?.youtube_price);
        if (yt !== undefined) return yt;
      }
      return d.price;
    },
    missing() {
      const f = cur();
      if (!f) return 'load';
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return '';
      if (!s.identity && !s.integrityError) return 'load';
      if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
      /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
      if (!s.form) return s.error ? '' : 'load';
      if (s.versionStale) return '';
      if (!textPolicy(s.form) || toolOf(s.form) !== f.tool) return 'off';
      if (!s.form.available) return 'unavailable';
      /* черновик не сохранился: кнопка «Сбросить черновик» (label, start) */
      if (f.draft.blocked()) return '';
      if (f.draft.restoring()) return 'load';
      /* варианты на месте: исход заказа ещё не известен или варианты пишутся — кнопка ведёт его (start) */
      if (inline(f) && (d.orderId || s.intent)) return '';
      return null;
    },
    ready() {
      const f = cur();
      if (!f) return 'load';
      const { s, d } = live(f);
      const form = s.form;
      if (!form || !textPolicy(form)) return 'off';
      /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
      if (f.bridge.pending()) return f.bridge.stalled() && s.error === TRANSPORT && !s.busy ? '' : 'upload';
      const keys = keysOf(f);
      const extra = { recording: h.REC.st !== 'idle', link: keys.url ? S.yt.url : undefined };
      /* права после перезагрузки подтверждает лист при нажатии «Создать» (start): здесь кнопка остаётся активной */
      const reason = textMissing(form, s.inputs, true, extra);
      if (reason) return reasonText(reason);
      const policy = textPolicy(form)!;
      if (keys.wishes && f.tool === 'seo') {
        const limit = wishesLimit(policy, toolField(form, keys.wishes), 300);
        if (Array.from(S.seo.wish).length > limit) return uiFormat('Пожелания длиннее {0} знаков', limit);
      }
      if (form.chargeable !== false && server.price() !== d.price) return uiText('Цена этого выбора пока не подтверждена');
      return '';
    },
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.form && !textPolicy(s.form) ? 'Создание сейчас недоступно' : s?.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем файл');
        case 'file':
          /* ссылки форма не публикует — «или вставьте ссылку» не говорим (родная форма) */
          return f && f.tool === 'stt' && !keysOf(f).url ? uiText(keysOf(f).video ? 'Загрузите запись или видео' : 'Загрузите запись') : '';
        default:
          return '';
      }
    },
    label() {
      const f = cur();
      if (!f) return null;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (f.draft.blocked()) return uiText('Сбросить черновик');
      /* варианты на месте: тот же ввод после готовых вариантов — «Ещё варианты · N C» (цена группы — политики формы, как у родной кнопки);
         неподтверждённая группа — «Продолжить проверку» тем же ключом */
      if (inline(f) && d.orderId && s.order?.status === 'uncertain') return uiText('Проверить ещё раз');
      if (inline(f) && d.orderId && s.order?.status === 'done' && f.res?.sig === h.sigOf(S[f.tool as 'pnew' | 'pfix'])) {
        const more = f.more.state();
        if (more.pending && !more.pending.child) return uiText('Продолжить проверку');
        const price = morePolicy(f).expected_price;
        if (moreReady(f) && typeof price === 'number')
          return s.form?.chargeable === false || price <= 0 ? uiText('Ещё варианты') : uiFormat('{0} · {1} C', uiText('Ещё варианты'), cNum(price));
      }
      if (s.form?.chargeable === false) return uiText(h.VERB()[h.kindNow()] ?? 'Создать');
      return null;
    },
    unit() {
      const f = cur() ?? frame;
      if (!f) return '';
      const form = formOf(f);
      if (form?.chargeable === false) return '';
      const kind = h.kindNow();
      const amount = kind === 'yt' ? (num(policyOf(f)?.youtube_price) ?? null) : toolPrice(f.app.price).amount;
      if (amount === null || !(amount > 0)) return '';
      return core().html.unit(amount, uiText(h.UNIT()[kind] ?? ''));
    },
    ytFile: () => null,
    upOpts: tool => ({
      ...(tool === 'stt' ? {} : { accept: ACCEPT[tool] }),
      /* свои строки рамки BotCore.upload («Загрузить», «Записать», «Заменить», «Убрать», отказы) — словарём профиля */
      tx: (template: string, ...values: unknown[]) =>
        /* рамка расшифровки (kind audio) принимает и видео: отказ по типу — как у шаблона (fill badf, pick ниже), а не «Нужна запись: MP3…» */
        tool === 'stt' && template === STT_BAD_AUDIO
          ? sttBad()
          : uiFormat(template, ...values),
      onPick(files, at) {
        const f = cur();
        if (!f || !files.length) return;
        pick(f, tool, files, at);
      },
    }),
    note(tool) {
      const f = cur() ?? frame;
      const form = formOf(f);
      if (!f || !form) return '';
      const keys = keysOf(f);
      const fields = (tool === 'stt' ? [keys.audio, keys.video] : tool === 'pvideo' ? [keys.video] : [keys.photos[0]])
        .map(key => (key ? toolField(form, key)?.upload : null))
        .filter((upload): upload is NonNullable<typeof upload> => !!upload);
      if (!fields.length) return '';
      const accept = [...new Set(fields.flatMap(upload => upload.accept))];
      const max = Math.max(...fields.map(upload => upload.max_bytes ?? 0));
      const kinds = mediaNote(accept, max || 200 * MB);
      if (tool === 'pvideo') return uiFormat('{0}, из длинного выберете {1} секунд', kinds.replace(/, [^,]*$/, ''), h.clipMax());
      return kinds;
    },
    remove(tool, i) {
      const f = cur();
      if (!f) return;
      if (tool === 'seo') {
        f.bridge.slots(trayKeys(f)).removeSlot(i ?? 0);
      } else if (tool === 'stt' && !S.stt.file) {
        /* в рамке ссылка, а не файл (шаблон: «Убрать» снимает её) */
        S.yt.url = '';
        S.yt.err = false;
        const input = document.getElementById('ttLink') as HTMLInputElement | null;
        if (input) input.value = '';
      } else {
        const keys = tool === 'stt' ? sttKeys(f) : [singleKey(f, tool)].filter((key): key is string => !!key);
        for (const key of keys) if (f.bridge.item(key) || f.svc.get().inputs[key]) f.bridge.remove(key);
        if (tool === 'pvideo') h.VT.close();
      }
      paintFiles(f);
      if (onForm()) {
        h.paintUp();
        h.ui().buzz();
      }
    },
    write() {
      const f = cur();
      if (!f || reading || !onForm()) return;
      const form = formOf(f);
      if (!form || f.form !== form || f.leaving) return;
      const { s, d } = live(f);
      /* варианты на месте: первая правка после готовых (или сбойных) вариантов закрывает тот заказ и возвращает форму с тем же вводом,
         правка остаётся (родная форма: editable) */
      if (inline(f) && d.orderId && ['done', 'failed'].includes(s.order?.status ?? '') && !s.busy) {
        const want = viewOf(f);
        if (sameInputs(writeText(form, want, s.inputs), s.inputs)) return;
        void inlineLeave(f).then(ok => {
          if (!ok || cur() !== f) return;
          writeView(f, want);
          apply(f, want);
          changed(f);
        });
        return;
      }
      writeView(f);
    },
    tones(list) {
      const f = cur() ?? frame;
      const form = formOf(f);
      const key = keysOf(f).tone;
      const values = form && key ? (toolField(form, key)?.values ?? []) : [];
      const shown = values.length ? list.filter(tone => values.includes(tone.id)) : list;
      /* копии со строками словаря профиля: TONES шаблона не меняется (его читает «Карточка для маркетплейса») */
      return shown.map(tone => ({ ...tone, n: uiText(tone.n), h: uiText(tone.h), t: uiText(tone.t), b: tone.b ? uiText(tone.b) : '' }));
    },
    items(list) {
      const f = cur() ?? frame;
      const form = formOf(f);
      if (!f || !form) return list;
      const keys = keysOf(f);
      const policy = policyOf(f);
      const field = (key: string | undefined) => (key ? toolField(form, key) : undefined);
      const KEY: Record<string, string | undefined> = {
        spk: keys.diarization,
        tc: keys.timestamps,
        lang: keys.language,
        ytkind: keys.ytKind,
        ytlang: keys.ytLang,
        market: keys.market,
        wish: keys.wishes,
        pstyle: keys.style,
        plang: keys.lang,
      };
      return list.flatMap(item => {
        const key = KEY[item.k];
        const own = field(key);
        if (!key || !own) return [];
        if (item.kind === 'toggle') return own.values.length && !(own.values.includes('true') && own.values.includes('false')) ? [] : [item];
        if (item.kind === 'choice') {
          const opts = (item.opts ?? []).filter(opt => !own.values.length || own.values.includes(opt[0]));
          return opts.length ? [{ ...item, opts }] : [];
        }
        if (item.kind === 'text') {
          const next: SettingItem = { ...item, max: policy ? wishesLimit(policy, own, item.max ?? 300) : item.max };
          if (policy?.improve_available !== true) delete next.improve;
          return [next];
        }
        return [item];
      });
    },
    tpl(html) {
      return server.has('own') ? html : html.replace(/<button class="chip own[^"]*"[\s\S]*?<\/button>/, '');
    },
    go(tool) {
      const f = cur();
      if (!f || tool === f.tool) return;
      openStudioTarget({ screen: 'text', options: { tool } });
    },
    gen() {
      const f = cur();
      if (f && inline(f)) void inlineCreate(f);
    },
    varsNote(r) {
      const f = cur() ?? frame;
      const order = f ? orderById(f, r.id) : null;
      const ours = !!order;
      /* что стало с деньгами — по чеку заказа (order.financial), не по виду сбоя: отказ модерации бывает с возвратом, сбой — со списанием */
      const receipt = ours ? tailReceipt(order) : null;
      const money =
        receipt?.amount == null || receipt.kind === 'free'
          ? ''
          : uiFormat(receipt.kind === 'refund' ? '{0} C вернулись на баланс' : '{0} C списаны', cNum(receipt.amount));
      const note = (kind: string, text: string) => core().html.note(kind, text);
      switch (r.st) {
        case 'mod':
          return note('bad', [uiText('Не прошло проверку: политику и запрещённые темы не пропускаем.'), money].filter(Boolean).join(' '));
        case 'fail':
          return note('warn', `${uiText('Сбой на нашей стороне')}${money ? ', ' + money : ''}. ${uiText('Попробуйте ещё раз')}`);
        case 'unk':
          return note('warn', uiText(ours ? 'Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.' : f?.svc.get().error || 'Заказ не подтверждён. Продолжение проверит прежний запрос с тем же ключом.'));
        case 'rej':
          return note('bad', uiText(r.error || 'Проверьте заполненные поля. Заказ не создан.'));
        case 'err':
          return core().html.note('warn', uiText('Не удалось открыть варианты.'), `<button type="button" class="lnk" data-tt-reload>${h.esc(uiText('Повторить'))}</button>`);
        default:
          return null;
      }
    },
    vrec(file) {
      const f = cur();
      if (!f) return;
      if (!file) {
        ups().pvideo.say(uiText('Запись недоступна. Загрузите видео.'));
        if (onForm()) h.paintUp();
        return;
      }
      pick(f, 'pvideo', [file]);
    },
    canRecord: () => !!(window.SteosUI as unknown as { canRecord?(): boolean }).canRecord?.(),
    micText: kind => uiText(kind === 'off' ? 'Запись здесь недоступна, загрузите файл' : 'Не удалось включить микрофон. Разрешите доступ или загрузите файл.'),
    recorded({ sec, blob, url }) {
      if (url)
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес записи больше не нужен: мост делает свой WAV */
        }
      if (onForm()) h.paintUp();
      const f = cur();
      const key = f ? keysOf(f).audio : undefined;
      if (!f || !key) return;
      if (!blob) return ups().stt.say(uiText('Не удалось прочитать запись, попробуйте ещё раз'));
      if (sec < 1) return;
      clearSource(f, key);
      void f.bridge.recorded(key, blob, h.recMax() * 1000);
    },
    camOff(tool) {
      const U = tool === 'seo' ? ups().seo : ups().pphoto;
      U.say(uiText('Камера недоступна. Загрузите файл.'));
    },
    shot(file, tool) {
      const f = cur();
      if (!f) return;
      pick(f, tool === 'seo' ? 'seo' : 'pphoto', [file]);
    },
    card(r) {
      const f = cur() ?? frame;
      return `<div class="tt-res">${f ? cardInner(f, r) : ''}</div>`;
    },
    plain() {
      const f = cur() ?? frame;
      const doc = f ? docOf(f) : null;
      return doc ? textDocumentPlain(doc) : '';
    },
    script() {
      const f = cur() ?? frame;
      const doc = f ? docOf(f) : null;
      return doc ? scriptOf(doc) : { text: '', speakers: [] };
    },
    steps() {
      const f = cur() ?? frame;
      const kind = sentKind(f);
      const form = formOf(f);
      const view = form ? readText(form, sentInputs(f)) : null;
      const step = (key: string, icon: string, run: string, done: string) => [key, icon, uiText(run), uiText(done)];
      switch (kind) {
        case 'stt': {
          const v = view?.tool === 'stt' ? view.v : null;
          return [
            step('file', 'upload', 'Загружаем файл', 'Файл загружен'),
            step('speech', 'mic', 'Распознаём речь', 'Речь распознана'),
            ...(v?.spk !== false ? [step('spk', 'users', 'Определяем, кто говорит', 'Собеседники подписаны')] : []),
            v?.tc !== false ? step('time', 'clock', 'Отмечаем время реплик', 'Время отмечено') : step('pack', 'doc', 'Собираем текст', 'Текст собран'),
          ];
        }
        case 'yt': {
          const y = view?.tool === 'stt' ? view.v.ytKind : 'full';
          return [
            step('get', 'tv', 'Получаем ролик', 'Ролик получен'),
            step('speech', 'mic', 'Распознаём речь', 'Речь распознана'),
            y === 'sum' ? step('pack', 'doc', 'Пишем пересказ', 'Пересказ готов') : y === 'th' ? step('pack', 'list', 'Выделяем тезисы', 'Тезисы готовы') : step('pack', 'doc', 'Собираем текст', 'Текст собран'),
          ];
        }
        case 'seo': {
          const photos = trayKeys(f ?? ({} as Frame)).some(key => !!sentInputs(f)[key]);
          return [
            step('text', 'shield', 'Проверяем данные', 'Данные проверены'),
            ...(photos ? [step('look', 'image', 'Смотрим фото', 'Фото изучено')] : []),
            step('write', 'edit', 'Пишем описание', 'Описание готово'),
            step('keys', 'hash', 'Подбираем ключевые слова', 'Ключевые слова подобраны'),
          ];
        }
        case 'pphoto':
          return [step('look', 'scan', 'Смотрим кадр', 'Кадр изучен'), step('write', 'wand', 'Пишем промпт', 'Промпт готов')];
        default:
          return [step('look', 'film', 'Смотрим ролик', 'Ролик изучен'), step('write', 'wand', 'Пишем промпт', 'Промпт готов')];
      }
    },
    runP(r) {
      return uiText(RUN_P[String(r.snap.tool)] ?? RUN_P.stt);
    },
    snap() {
      const f = cur() ?? frame;
      const kind = sentKind(f);
      const form = formOf(f);
      const inputs = sentInputs(f);
      const view = form ? readText(form, inputs) : null;
      const keys = keysOf(f);
      /* файл в памяти — только если он и есть файл заказа */
      const own = (key: string | undefined, file: TextFile | null) => (key && file && f?.bridge.item(key)?.uploadId === inputs[key] ? { ...file } : null);
      const s: Record<string, unknown> = { tool: kind, v: 0 };
      if (kind === 'stt') {
        const v = view?.tool === 'stt' ? view.v : null;
        const key = [keys.audio, keys.video].find(k => k && inputs[k]);
        const file = own(key, S.stt.file) ?? { name: uiText('Запись'), size: 0, url: '', dur: 0, kind: key && key === keys.video ? 'video' : 'audio', peaks: flat() };
        Object.assign(s, { file, lang: v?.lang ?? 'auto', spk: v?.spk ?? true, tc: v?.tc ?? true });
      } else if (kind === 'yt') {
        const v = view?.tool === 'stt' ? view.v : null;
        Object.assign(s, { id: youtubeId(v?.url ?? ''), kind: v?.ytKind ?? 'full', lang: v?.ytLang ?? 'src' });
      } else if (kind === 'seo') {
        const v = view?.tool === 'seo' ? view.v : null;
        const tray = f ? trayKeys(f) : [];
        const first = tray.find(key => inputs[key]);
        const file = first ? (S.seo.files.find(x => f?.bridge.slots(tray).list().find(item => item.id === x.mid)?.uploadId === inputs[first]) ?? null) : null;
        Object.assign(s, { file: file ? { ...file } : null, n: tray.filter(key => inputs[key]).length, text: v?.text ?? '', market: v?.market ?? '', tone: v?.tone ?? '', wish: v?.wish ?? '' });
      } else if (kind === 'pphoto') {
        const v = view?.tool === 'pphoto' ? view.v : null;
        Object.assign(s, { file: own(keys.photos[0], S.pphoto.file), style: v?.style ?? 'detail', lang: v?.lang ?? 'ru' });
      } else {
        const v = view?.tool === 'pvideo' ? view.v : null;
        const clip = own(keys.video, S.pvideo.clip);
        Object.assign(s, { clip: clip ? { ...clip, dur: h.clipDur(S.pvideo.clip) } : null, style: v?.style ?? 'detail', lang: v?.lang ?? 'ru' });
      }
      return s;
    },
    art(sn) {
      const img = (src: string) => `<img src="${h.esc(src)}" alt="">`;
      const doc = { html: h.docArt(), size: { w: 188, h: 236 }, art: true };
      const size = (file: TextFile, mw: number, mh: number) => core().frameSize(`${file.w || 3}:${file.h || 4}`, mw, mh);
      switch (sn.tool) {
        case 'stt': {
          const file = sn.file as TextFile | null;
          if (file?.kind === 'video' && file.poster && file.poster !== NO_POSTER) return { html: img(file.poster), size: size(file, 236, 220) };
          return { html: h.waveArt({ peaks: file?.peaks?.length ? file.peaks : flat(), dur: file?.dur }), size: { w: 236, h: 148 }, art: true };
        }
        case 'seo':
        case 'pphoto': {
          const file = sn.file as TextFile | null;
          if (!file?.url) return doc;
          return sn.tool === 'seo' ? { html: img(file.url), size: size(file, 200, 240) } : { html: img(file.url), size: size(file, 236, 280) };
        }
        case 'pvideo': {
          const clip = sn.clip as TextFile | null;
          return clip?.poster && clip.poster !== NO_POSTER ? { html: img(clip.poster), size: size(clip, 236, 280) } : doc;
        }
        default:
          /* ссылка YouTube: обложки ролика без данных сервера нет (Q-T1) — лист текста */
          return doc;
      }
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const kind = (r.snap.tool as string) || sentKind(f);
      const R = h.R[kind] ?? h.R.stt;
      const steps = tailSteps(order);
      /* отказ по входу (stt_no_speech): свой заголовок и причина родного хвоста, повтор с тем же файлом не поможет */
      const input = textTailFail(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: input
          ? { modH: input.head, techH: input.head, notMade: R.notMade, modWhy: input.why, techWhy: input.why }
          : { modH: R.modH, techH: R.techH, notMade: R.notMade },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      return receipt(r, f?.svc.get().order ?? null, { cheap: false, where: '' });
    },
    doneText(r) {
      const f = cur() ?? frame;
      const order = f?.svc.get().order ?? null;
      const kind = String(r.snap.tool);
      /* родной хвост: бот уже прислал (доставка sent, delivered) или пришлёт; не присылает — текст доставки (tail-done) */
      const sent = ['sent', 'delivered'].includes(String(record(record(order).delivery).state));
      const head = (sent ? DONE_SENT : DONE_SOON)[kind] ?? (sent ? DONE_SENT : DONE_SOON).yt;
      const extra = kind === 'seo' ? ' ' + uiText('Каждый блок копируется отдельно.') : kind[0] === 'p' ? ' ' + uiText('Можно сразу создать по нему фото.') : '';
      return doneP(order, esc(uiText(head) + extra), 'tt');
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        if (inline(f)) server.gen();
        else h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (f.draft.blocked()) {
        void f.draft.reset();
        return;
      }
      if (needsRights(f)) {
        confirmRights(f, () => server.start(btn));
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      if (inline(f)) server.gen();
      else h.tail.start();
    },
    leave(go, replace) {
      const f = cur();
      if (!f) return;
      const key = singleKey(f, (f.tool === 'pnew' || f.tool === 'pfix' ? 'stt' : f.tool) as UpTool) ?? '';
      void f.driver.leave(go, replace, key);
    },
    click(d, el, r) {
      const f = cur();
      const s = f?.svc.get();
      /* «Ещё вариант» (D10, docs/03-api.md §12.3): к форме с теми же настройками, без запроса — новый заказ создаёт «Создать» */
      if (d.ttVar !== undefined) {
        if (f && r?.st === 'done') server.leave(() => h.tail.exit(() => h.paintPage()), false);
        return true;
      }
      if (d.ttChat !== undefined) {
        if (r?.st === 'done') chatClick({ order: s?.order, el, text: CHAT_TIP[String(r.snap.tool)] ?? CHAT_TIP.yt, tip: (at, text) => h.ui().tip(at, text) });
        return true;
      }
      if (d.ttSettings !== undefined) {
        openSettings();
        return true;
      }
      if (!f) return false;
      if (d.ttReload !== undefined) {
        f.results.retry(inline(f) ? orderById(f, f.res?.id) : s?.order);
        return true;
      }
      /* «Скопировать» и «Скачать» итога — текст документа сервера (шаблон берёт его через resText → plain); документа ещё нет — подсказка */
      if ((d.ttCopy !== undefined || d.ttDl !== undefined) && r?.st === 'done' && !docOf(f)) {
        tip(el, 'Загружаем…');
        return true;
      }
      if (r?.st === 'done' && nextAction(d, el, docOf(f))) return true;
      /* варианты на месте: «Использовать в Фото / Видео», «Улучшить» (у музыки и текста «Создать промпт») — другое приложение с текстом варианта */
      if (d.ttVuse !== undefined || d.ttVfix !== undefined) {
        const res = f.res;
        const v = res?.st === 'done' ? res.vars?.[Number(d.ttVuse ?? d.ttVfix)] : undefined;
        if (!res || !v) return true;
        const target = res.snap.target;
        if (d.ttVuse !== undefined)
          openStudioTarget({ screen: target === 'video' ? 'video' : 'photo', options: { mode: target === 'video' ? 'text' : 'generate', description: clipText(v.t, PROMPT_MAX) } });
        /* «Улучшить» у шаблона — тот же экран «Текста» (setTool на месте), здесь — другой кадр: ret — «Назад» с него вернёт к этому кадру
           «Создать промпт» (возврат делает студия, formHistory), а не на раздел */
        else openStudioTarget({ screen: 'text', ret: true, options: { mode: 'improve', text: v.t, kind: target } });
        return true;
      }
      if (d.ttRetry === undefined && d.ttRchk === undefined && d.ttFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.ttRetry !== undefined) h.tail.retry();
      else if (d.ttRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
      return true;
    },
    improve: (text, o) => improveInline(text, o),
  };

  /** Источник расшифровки один: новый файл снимает другой файл и ссылку (TextSttSource) */
  function clearSource(f: Frame, keep: string) {
    for (const key of sttKeys(f)) if (key !== keep && (f.bridge.item(key) || f.svc.get().inputs[key])) f.bridge.remove(key);
    if (S.yt.url) {
      S.yt.url = '';
      S.yt.err = false;
    }
  }
  /** Отказ рамки расшифровки по типу файла: видео форма принимает — «запись или видео» */
  function sttBad() {
    const f = cur() ?? frame;
    return uiText(f && keysOf(f).video ? 'Нужна запись или видео' : 'Нужна аудиозапись');
  }
  /** Файлы из рамки инструмента → мост загрузки */
  function pick(f: Frame, tool: UpTool, files: File[], at?: number) {
    const keys = keysOf(f);
    if (tool === 'seo') {
      const tray = trayKeys(f);
      if (tray.length) void f.bridge.slots(tray).pickSlots(files, at);
      return;
    }
    const file = files[0];
    if (!file) return;
    if (tool === 'stt') {
      const type = file.type.toLowerCase();
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const video = type.startsWith('video/') || (!type && ['mp4', 'mov', 'm4v', 'webm', 'qt'].includes(ext) && !!keys.video && !keys.audio);
      const key = video ? keys.video : keys.audio;
      if (!key) {
        ups().stt.say(sttBad());
        return;
      }
      clearSource(f, key);
      void f.bridge.pick(key, file);
      return;
    }
    const key = singleKey(f, tool);
    if (!key) return;
    void f.bridge.pick(key, file);
  }

  /* ---- смена языка профиля (i18n: событие steos:locale) ---- */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    translate();
    if (!f || !root || !h.mounted()) return;
    if (f.form) fillLists(f);
    if (r) h.tail.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me (пополнение, слияние) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.syncFoot();
  });

  instance = { bind };
  return server;
}

/** ENGINES.text.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindTextEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Знак заказа над меню: у инструментов с хвостом — «Текст», у вариантов на месте («Создать промпт», «Улучшить промпт») знака нет (родная форма) */
export function textBadgeKind(s: { form: StudioForm | null }, app: StudioApp): 'text' | null {
  return inlineTool(toolOf(s.form) ?? toolOfEntry(app.entry)) ? null : 'text';
}

/** Экран «text» реестра движков студии: движок шаблона window.BotText, атрибуты data-tt-*, не боковой; знак — по инструменту */
export const TEXT_ENGINE: EngineScreen = { global: 'BotText', prefix: 'tt', side: false, kind: (s, app) => textBadgeKind(s, app), bind: bindTextEngine };

export function registerTextEngine() {
  ENGINES.text = TEXT_ENGINE;
}
